import { BaseApiClient } from './base.client';

export interface SearchEventsParams {
  search?: string;
  // Set when the user picks a specific event from the search-bar dropdown.
  // The listing page filters by this id and returns exactly one event.
  eventId?: number;
  cityId?: number;
  countryId?: number;
  // Multi-select filters: comma-separated id list (e.g. "1,2,3"). Single
  // values like "5" are accepted too. The backend uses $in when more than
  // one id is supplied.
  eventTypeId?: number | string;
  dateFrom?: string;
  dateTo?: string;
  minPrice?: number;
  maxPrice?: number;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  participantId?: number | string;
  venueId?: number;
  tournamentId?: number | string;
  // Comma-separated subset of 'home', 'away', 'homeAway'.
  teamType?: string;
}
 
export interface Event {
  id: number;
  name: string;
  homeTeam?: { id: number; name: string };
  awayTeam?: { id: number; name: string };
  participants?: { id: number; name: string };
  eventType: { id: number; name: string; isTeamSport: boolean; concert: boolean };
  tournament?: { id: number; name: string; isGroupEvent: boolean };
  dateOfEvent: string;
  timeOfEvent: string;
  finalDate: boolean;
  finalTime: boolean;
  dateTimeBoundaries?: string;
  country: { id: number; name: string };
  city: { id: number; name: string };
  venue: {
    id: number;
    name: string;
    mapUrl?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
  };
  eventUrl?: string;
  availableCategoriesQuantity: number;
  minTicketPrice: { price: number; serviceFee?: number; currency: string };
  soldOut?: boolean;
  score?: number;
}

export interface PaginatedEvents {
  data: Event[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface Ticket {
  id: number;
  eventId: number;
  categoryName: string;
  splittedCategoryName?: {
    main?: string;
    secondary?: string;
    packageDetails?: string;
  };
  sittingArrangements?: string;
  categoryRestrictions?: string[];
  purchaseAlert?: string;
  importantNotes?: string;
  /** Customer-facing display price: ticket cost + BIP commission
   *  (incl. the VAT on that commission). This is what the listing and
   *  detail pages show. The cost-commission slice is `transactionFee`
   *  and is disclosed only at checkout. */
  price: number;
  /** Unrounded twin of `price`. Use for `qty × price + delivery`
   *  arithmetic and round once at the end, so the figure on screen
   *  matches `DOCS/Sports commission.xlsx` and the amount charged. */
  priceExact?: number;
  serviceFee: number;
  /** Cost-commission slice of the per-ticket price, incl. its VAT —
   *  the "Transaction Fee" row on the checkout page. `price +
   *  transactionFee` is what the customer is actually charged per
   *  ticket. Absent on responses from a service that pre-dates the
   *  2026-09-09 pricing split, in which case `price` already carries
   *  the full amount — treat a missing value as 0 and do NOT render a
   *  fee row. */
  transactionFee?: number;
  /** Unrounded twin of `transactionFee`. `priceExact +
   *  transactionFeeExact` is exactly the final price, so summing the
   *  exact values and rounding once always reconciles to the Total. */
  transactionFeeExact?: number;
  netPrice?: number;
  priceComments?: string;
  currency: string;
  availableSellingQuantities: number[];
  tags?: Array<{ non_plastic?: boolean }>;
  immediateConfirmation: boolean;
  immediateConfirmationText?: string;
  /**
   * Per-ticket attendee-field requirement flags from SE365. Each
   * value is `true` / `false` — but the wire format has been
   * observed in BOTH boolean (`true`) and string (`"true"`) shapes
   * depending on the connector path, so the type allows either.
   * Consumers should treat the boolean `true` and the string
   * `"true"` (case-insensitive) as required; everything else as
   * optional.
   */
  requiredPurchaseFields?: {
    nationalityCountryId?: boolean | string;
    birthDate?: boolean | string;
    fullName?: boolean | string;
    passportNumber?: boolean | string;
    cityOfBirth?: boolean | string;
  };
  deliveryMethods: Array<{
    id: number;
    name: string;
    description?: string;
    /** Commission-applied display price, BIP layer only — matches the
     *  ticket `price` above so the "Includes £X delivery fee" footnote
     *  agrees with the row it sits under. Pre-commission SE365 amount
     *  is exposed as `rawPrice` alongside for audit / server
     *  reconciliation. */
    price: number;
    rawPrice?: number;
    /** Cost-commission slice of this delivery fee, incl. its VAT.
     *  Rolls into the checkout "Transaction Fee" row. See
     *  `Ticket.transactionFee` for the legacy-response caveat. */
    transactionFee?: number;
    /** Unrounded twin of `transactionFee` — sum exact values, round once. */
    transactionFeeExact?: number;
    /** Unrounded twin of `price`. Sum exact components and round
     *  ONCE at display time — adding pre-rounded parts loses up to
     *  half a penny each and drifts from the commission sheet. */
    priceExact?: number;
  }>;
  onHold24Hours?: {
    status: boolean;
    publicMessage?: string;
  };
  isVipPackage: boolean;
}

export interface GuestInfo {
  firstName: string;
  lastName: string;
  nationalityCountryId?: string;
  birthDate?: string;
  passportNumber?: string;
  cityOfBirth?: string;
}

export interface CreateBookingRequest {
  eventId: number;
  expectedTotal?: number;
  /** Currency the customer wants to be charged in (e.g. their selected GBP). */
  displayCurrency?: string;
  /**
   * Mongo `_id` of the logged-in member, sourced from `authClient.getMe()`.
   * Omit for guest checkouts — the booking will simply not be tied to a
   * member account. The backend trims and validates this server-side.
   */
  memberId?: string;
  tickets: Array<{
    ticketId: number;
    quantity: number;
    deliveryMethodId?: number;
  }>;
  customerInfo: {
    fullName: string;
    firstName?: string;
    lastName?: string;
    email: string;
    phone: string;
    address1?: string;
    address2?: string;
    city?: string;
    country?: string;
    state?: string;
    nationalityCountryId?: string;
    birthDate?: string;
    passportNumber?: string;
    cityOfBirth?: string;
  };
  /**
   * Booker — the person actually placing the booking. Kept separate
   * from `customerInfo` (which represents the lead traveller) so the
   * "I'm booking for someone else" flow doesn't overwrite the
   * customer record with the booker's name. Both names match for
   * self-bookings; only the booker name diverges when booking for
   * a third party.
   */
  booker?: {
    firstName?: string;
    lastName?: string;
    fullName?: string;
  };
  /**
   * `true` when the customer ticked the "I understand and agree to
   * the Important Notice" checkbox on the checkout page. Sent only
   * when at least one selected ticket carries a SE365
   * `purchaseAlert`.
   */
  purchaseAlertConfirmation?: boolean;
  guests?: GuestInfo[];
}

// Matches the BACKEND enum exactly (services/sports-events-service/.../booking.schema.ts) — this
// was previously lowercase (`'cancelled'`) and didn't match real stored/returned data
// (`'Cancelled'`), so any `booking.status === BookingStatus.CANCELLED`-style check against real
// data could never be true. Not referenced anywhere as an enum value (only as a type), so
// correcting the casing here is safe.
export enum BookingStatus {
  PENDING = 'Pending',
  ON_HOLD = 'OnHold',
  CONFIRMED = 'Confirmed',
  CANCELLED = 'Cancelled',
  EXPIRED = 'Expired',
  /** Customer's payment attempt failed (card declined / PI cancelled). */
  FAILED = 'Failed',
  /** Payment succeeded but SE365 never produced a usable order — the
   *  services write `'Booking_Failed'`; compare case-insensitively. */
  BOOKING_FAILED = 'Booking_Failed',
}

export enum PaymentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

export interface Booking {
  id: string;
  eventId: number;
  eventName: string;
  tickets: Array<{
    ticketId: number;
    categoryName: string;
    quantity: number;
    pricePerTicket: number;
    serviceFee: number;
    currency: string;
    deliveryMethodId?: number;
    deliveryMethodPrice?: number;
    /** Per-ticket delivery fee frozen in `displayCurrency` at
     *  booking-create time — the amount that actually flowed
     *  through Stripe. Prefer this over `deliveryMethodPrice` on
     *  any confirmation-side surface so the disclosure matches
     *  what the customer was charged; fall back to
     *  `deliveryMethodPrice` for legacy bookings that pre-date
     *  the snapshot. */
    displayDeliveryMethodPrice?: number;
    /** Per-ticket price frozen in `displayCurrency` at booking-create
     *  time. Carries the BIP-commission layer only (pricing spec
     *  2026-09-09) — the cost layer lives in the booking-level
     *  `displayTransactionFeeSubtotal`. Prefer this over
     *  `pricePerTicket` on confirmation-side surfaces so the figure
     *  matches what the customer was charged without re-running FX;
     *  fall back to `pricePerTicket + serviceFee` for legacy rows. */
    displayPricePerTicket?: number;
    /** Seat-type label captured at booking-create time
     *  (e.g. "Up to 2 together"). */
    sittingArrangements?: string;
  }>;
  customerInfo: {
    fullName: string;
    firstName?: string;
    lastName?: string;
    email: string;
    phone: string;
    address1?: string;
    address2?: string;
    city?: string;
    country?: string;
    state?: string;
    nationalityCountryId?: string;
    birthDate?: string;
    passportNumber?: string;
    cityOfBirth?: string;
  };
  guests?: GuestInfo[];
  status: BookingStatus;
  paymentStatus: PaymentStatus;
  totalAmount: number;
  currency: string;
  /** Currency the customer paid in (Stripe charge currency). */
  displayCurrency?: string;
  /** Locale-formatted symbol for `displayCurrency` (e.g. "£", "€", "$"). */
  displayCurrencySymbol?: string;
  /** `totalAmount` converted into `displayCurrency` (Stripe charge amount). */
  displayTotalAmount?: number;
  /**
   * Checkout / confirmation price breakdown in `displayCurrency`,
   * frozen at booking-create time (pricing spec 2026-09-09). The
   * three sum to `displayTotalAmount`:
   *
   *   displayTicketSubtotal         — tickets + BIP commission
   *   displayDeliverySubtotal       — delivery + BIP commission
   *   displayTransactionFeeSubtotal — cost commission ("Transaction Fee")
   *
   * Bookings created before that date bake the cost commission into
   * `displayTicketSubtotal` and carry no
   * `displayTransactionFeeSubtotal` — render the total only for
   * those rather than assuming a zero fee.
   */
  displayTicketSubtotal?: number;
  displayDeliverySubtotal?: number;
  displayTransactionFeeSubtotal?: number;
  holdExpiresAt?: string;
  stripeSessionId?: string;
  confirmedAt?: string;
  cancelledAt?: string;
  /** Set when `status === 'Failed'` — the booking couldn't be completed,
   *  whatever the reason (payment declined, or SE365 rejected/nullified
   *  the order). Read `paymentStatus` to tell "never charged" from
   *  "charged, refund owed". */
  failedAt?: string;
  CreatedDate: string;
  UpdatedDate: string;
  /** true = Immediate Confirmation; false = Offline Stock (manual processing) */
  immediateConfirmation: boolean;
  /** SE365's own order id (e.g. 242760). Used as the customer-visible
   *  "Booking confirmation number" once the order is accepted. */
  sportsEvents365OrderId?: string;
  /** Latest SE365 order status — `"Pending"` | `"Confirmed"` | `"Cancelled"`. */
  sportsEvents365OrderStatus?: string;
  /** Human-readable fallback id (e.g. "BSP1001") minted from our own counter when a booking
   *  transitions to a failed/declined state — i.e. exactly when SE365 never gave us a real order
   *  id. `null`/absent on successful bookings (SE365 owns the confirmed-order id there). Use as
   *  the "userReservationId" fallback wherever `sportsEvents365OrderId` is missing. */
  reservationId?: string;
  eventDetail?: any;
  /** Raw per-ticket rows (sittingArrangements, categoryName, immediateConfirmation, …)
   *  as captured at booking-create time — manage-bookings Sports/Shows detail view. */
  ticketDetail?: any[];
  /** Combined event date + time. */
  performanceDate?: string;
  /** Member id of the logged-in customer at booking time (absent for guest checkouts). */
  memberId?: string;
  cancellationReason?: string;
}

/** One row in the manage-bookings "Sports"/"Concerts" tabs list. `bookingType` reflects
 *  whichever domain the owning client instance serves (`sportsEventsClient` → 'sports',
 *  `concertsEventsClient` → 'concerts') — see `SportsEventsClient`'s constructor. */
export interface SportsBookingListItem extends Booking {
  bookingType: 'sports' | 'concerts';
}

export interface SportsBookingsListResponse {
  records: SportsBookingListItem[];
  total: number;
  limit: number;
  page: number;
  totalPages: number;
}

export interface PaginatedTickets {
  data: Ticket[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CheckoutSession {
  sessionId: string;
  url: string;
}

export interface PaymentIntent {
  clientSecret: string;
  paymentIntentId: string;
}

export interface TopCity {
  id: number;
  name: string;
  country: { id: number; name: string };
}

export interface TopTournament {
  id: number;
  name: string;
  eventType?: { id: number; name: string };
}

export interface FilterOptionItem {
  id: number;
  name: string;
  count?: number;
}

export interface FilterOptions {
  cities: FilterOptionItem[];
  sports: FilterOptionItem[];
  teams: FilterOptionItem[];
  tournaments: FilterOptionItem[];
  priceRange: { min: number; max: number };
}

export interface CurrencyItem {
  CurrencyCode: string;
  CurrencyName: string;
  CurrencySymbol: string;
  CountryName?: string;
  IsMain: boolean;
}

export interface Country {
  id: number;
  name: string;
}

/** One show in the "Nearest shows" cross-sell list. */
export interface NearbyShow {
  name: string;
  slug: string;
  image: string | null;
  venueName: string | null;
  venueCity: string | null;
  venueState?: string | null;
  distanceMiles: number | null;
  originalPrice: number;
  originalCurrency: string;
  finalPrice: number;
  finalCurrency: string;
  conversionRate: number;
  rating: number | null;
  reviewCount: number | null;
  /** Short marketing tagline from the legacy CMS show doc. */
  tagLine?: string | null;
  /** Legacy CMS city ObjectId — drives the explore-button deep link. */
  venueCityId?: string | null;
  /** Sports event date in `DD/MM/YYYY` — used to build deep links to the
   *  show's booking page so the show date matches the sports event date. */
  dateOfEvent?: string;
  /** Backend's `IsActive` flag, passed through as confirmation the
   *  `IsActive: true` query filter applied — always `true` in practice. */
  IsActive?: boolean;
}

/** One sport event in the "Nearby sports" cross-sell list. */
export interface NearbySportEvent {
  id: number;
  name: string;
  eventType: { id: number; name: string };
  tournament?: { id: number; name: string } | null;
  city: { id: number; name: string };
  country: { id: number; name: string };
  dateOfEvent: string; // DD/MM/YYYY
  timeOfEvent: string;
  // Matches the actual `/nearby-sports` response field name
  // (`distanceInMiles`, not `distanceMiles` — that's `NearbyShow`'s
  // field name, a different endpoint with its own shape).
  distanceInMiles?: number | null;
  minTicketPrice: { price: number | null; currencySymbol: string };
  detailPageUrl?: string | null;
}

/** One hotel in the "Nearest hotels" cross-sell list. Reserved — backend
 *  currently returns []; populated once a hotels data source is wired in. */
export interface NearbyHotel {
  name: string;
  city: string;
  state?: string | null;
  distanceFromGeo?: number | null;
  distanceKm: number | null;
  rating: number | null;
  detailPageUrl?: string | null;
  reviewCount: number | null;
  finalPrice: number;
  finalCurrency: string;
  imageUrl: string | null;
  bookingUrl: string | null;
}

export class SportsEventsClient extends BaseApiClient {
  /** Which domain this instance serves — stamped onto every `getBookings()` row's
   *  `bookingType` so the manage-bookings page (and anything else merging Sports +
   *  Concerts results together) can tell them apart despite sharing this one class. */
  private readonly bookingKind: 'sports' | 'concerts';

  constructor(baseUrl?: string, bookingKind: 'sports' | 'concerts' = 'sports') {
    // Env-driven base URL — no static fallback.
    super(baseUrl || process.env.NEXT_PUBLIC_SPORTS_EVENTS_API_URL);
    this.bookingKind = bookingKind;
  }

  /** Send the `member_d` session cookie on every call so the member-scoped bookings list can
   *  resolve the memberId server-side (`MemberSessionService`), same convention as `HotelsClient`
   *  / `ShowsClient`. */
  protected override async request<T>(
    endpoint: string,
    options?: RequestInit & { next?: { revalidate?: number | false; tags?: string[] }; timeoutMs?: number },
  ): Promise<T> {
    return super.request<T>(endpoint, { ...(options || {}), credentials: 'include' });
  }

  // Event Types
  async getEventTypes(): Promise<{ id: number; name: string }[]> {
    return this.get<{ id: number; name: string }[]>('/event-types');
  }

  // Cities
  async getTopCities(): Promise<TopCity[]> {
    return this.get<TopCity[]>('/cities/top');
  }

  // Tournaments
  async getTopTournaments(): Promise<TopTournament[]> {
    return this.get<TopTournament[]>('/tournaments/top');
  }

  // Events
  async searchEvents(params?: SearchEventsParams): Promise<PaginatedEvents> {
    return this.get<PaginatedEvents>('/events', params);
  }

  async getFilterOptions(params?: SearchEventsParams): Promise<FilterOptions> {
    return this.get<FilterOptions>('/events/filter-options', params);
  }

  async getEventById(id: number): Promise<Event> {
    return this.get<Event>(`/events/${id}`);
  }

  /** Cross-sell   mmm payload: up to N shows on the event's date in its venue city,
   *  ranked by haversine distance from the venue. Drives the "Nearest shows"
   *  section on the sports detail and booking-success pages. */
  async getNearbyShows(
    eventId: number,
    params?: { currency?: string; limit?: number },
  ): Promise<NearbyShow[]> {
    const qs: Record<string, string> = {};
    if (params?.currency) qs.currency = params.currency;
    if (params?.limit !== undefined) qs.limit = String(params.limit);
    return this.get<NearbyShow[]>(`/events/${eventId}/nearby-shows`, qs);
  }

  /** Stub for parity with shows — currently returns []. Reserved for the
   *  hotels data source (booking provider or local hotels collection). */
  async getNearbyHotels(eventId: number): Promise<NearbyHotel[]> {
    return this.get<NearbyHotel[]>(`/events/${eventId}/nearby-hotels`);
  }

  /** Nearby sport events for a given geo-coordinate — drives the cross-sell
   *  section on restaurant detail pages. Calls /nearby-sports (no events prefix). */
  async getNearbySports(params: {
    latitude: number;
    longitude: number;
    distance?: number;
    limit?: number;
    sortBy?: 'distance';
    sortOrder?: 'asc' | 'desc';
    dateFrom?: string;
    userCurrency?: string;
  }): Promise<{ success: boolean; data: NearbySportEvent[]; meta: { totalRecords: number } }> {
    const qs: Record<string, string> = {
      latitude: String(params.latitude),
      longitude: String(params.longitude),
    };
    if (params.distance !== undefined) qs.distance = String(params.distance);
    if (params.limit !== undefined) qs.limit = String(params.limit);
    if (params.sortBy) qs.sortBy = params.sortBy;
    if (params.sortOrder) qs.sortOrder = params.sortOrder;
    if (params.dateFrom) qs.dateFrom = params.dateFrom;
    if (params.userCurrency) qs.userCurrency = params.userCurrency;
    return this.get<{ success: boolean; data: NearbySportEvent[]; meta: { totalRecords: number } }>(
      '/nearby-sports',
      qs,
    );
  }

  /** Nearby concert events for a given geo-coordinate — powers the
   *  Nearby-concerts strip on sports / restaurants cross-sell.
   *  concerts-service mounts the route at `/bip-api/concerts/nearby-concerts`
   *  (SINGLE domain segment, mirrors `/nearby-sports`) so this client
   *  MUST be constructed with `NEXT_PUBLIC_CONCERTS_API_URL` — a
   *  sports base URL would 404. Shape matches `getNearbySports` — same
   *  upstream code path, filtered to concerts on the server. */
  async getNearbyConcerts(params: {
    latitude: number;
    longitude: number;
    distance?: number;
    limit?: number;
    sortBy?: 'distance';
    sortOrder?: 'asc' | 'desc';
    dateFrom?: string;
    userCurrency?: string;
  }): Promise<{ success: boolean; data: NearbySportEvent[]; meta: { totalRecords: number } }> {
    const qs: Record<string, string> = {
      latitude: String(params.latitude),
      longitude: String(params.longitude),
    };
    if (params.distance !== undefined) qs.distance = String(params.distance);
    if (params.limit !== undefined) qs.limit = String(params.limit);
    if (params.sortBy) qs.sortBy = params.sortBy;
    if (params.sortOrder) qs.sortOrder = params.sortOrder;
    if (params.dateFrom) qs.dateFrom = params.dateFrom;
    if (params.userCurrency) qs.userCurrency = params.userCurrency;
    return this.get<{ success: boolean; data: NearbySportEvent[]; meta: { totalRecords: number } }>(
      '/nearby-concerts',
      qs,
    );
  }

  /** Geo-based nearby shows — drives the cross-sell section on restaurant
   *  detail pages where no event ID is available. */
  async getNearbyShowsByGeo(params: {
    latitude: number;
    longitude: number;
    distance?: number;
    limit?: number;
    dateFrom?: string;
    userCurrency?: string;
  }): Promise<{ shows: NearbyShow[]; total: number }> {
    const qs: Record<string, string> = {
      latitude: String(params.latitude),
      longitude: String(params.longitude),
    };
    if (params.distance !== undefined) qs.distance = String(params.distance);
    if (params.limit !== undefined) qs.limit = String(params.limit);
    if (params.dateFrom) qs.dateFrom = params.dateFrom;
    if (params.userCurrency) qs.userCurrency = params.userCurrency;
    return this.get<{ shows: NearbyShow[]; total: number }>('/events/nearby-shows-geo', qs);
  }

  async getSearchResultData(searchStr: string): Promise<any> {
    return this.post<any>('/events/search-event-data', { searchStr });
  }

  async getEventTickets(eventId: number, page = 1, limit = 5): Promise<PaginatedTickets> {
    return this.get<PaginatedTickets>(`/events/${eventId}/tickets`, { page, limit });
  }

  /** Fetch ALL tickets across all pages (limit 100 per page). Returns a flat array. */
  async getAllEventTickets(eventId: number): Promise<Ticket[]> {
    const allTickets: Ticket[] = [];
    let page = 1;
    const limit = 100;
    let hasMore = true;
    while (hasMore) {
      const result = await this.getEventTickets(eventId, page, limit);
      allTickets.push(...result.data);
      hasMore = page < result.totalPages;
      page++;
    }
    return allTickets;
  }

  async subscribeTicketNotification(
    eventId: number,
    email: string,
    eventName?: string,
  ): Promise<{ message: string; displayMessage: string }> {
    return this.post('/events/ticket-notify', { eventId, email, eventName });
  }

  /** Fetch long text from SE365 API by IDs */
  async getLongText(ids: number[]): Promise<any> {
    return this.post<any>('/events/long-text', { id: ids });
  }

  /** Look up a city from the CountryCity collection by name */
  async lookupCity(name: string): Promise<any> {
    return this.get<any>('/events/lookup-city', { name });
  }

  /**
   * Resolve the city-type `SearchLocation` row by city name. Used
   * by the sports SearchBar / city listing page to seed cross-
   * domain recent-search localStorage entries.
   *
   * Response envelope:
   *   - `{ message: 'success', data: <doc> }`           — matched
   *   - `{ message: 'not_found', data: null, error? }`  — no row
   *   - `{ message: 'error', data: null, error }`       — exception
   *
   * On success, `data` carries the SearchLocation doc with the
   * hotels `dest_id` (as `_id`), the CountryCity ObjectId (as
   * `destination_id`), and the city/country/state strings.
   */
  async getCityDestId(cityName: string): Promise<{
    message: 'success' | 'not_found' | 'error';
    data: Record<string, any> | null;
    error?: string;
  }> {
    return this.get<{
      message: 'success' | 'not_found' | 'error';
      data: Record<string, any> | null;
      error?: string;
    }>('/events/city-dest-id', { cityName });
  }

  /** Subscribe email to newsletter */
  async subscribe(email: string, bookingId?: string): Promise<{ message: string }> {
    return this.post<{ message: string }>('/bookings/subscribe', { email, bookingId });
  }

  // Bookings
  async createBooking(data: CreateBookingRequest): Promise<Booking> {
    return this.post<Booking>('/bookings', data);
  }

  /** Return the customerInfo from the most recent booking under the given
   *  email — drives the checkout page's auto-fill so a returning customer
   *  doesn't retype address / city / phone. `customerInfo: null` means
   *  the email has never booked; the caller should skip prefill silently. */
  async getCustomerProfile(
    email: string,
  ): Promise<{ customerInfo: Booking['customerInfo'] | null }> {
    return this.get<{ customerInfo: Booking['customerInfo'] | null }>(
      '/bookings/customer-profile',
      { email },
    );
  }

  /** Member-scoped bookings list (manage-bookings "Sports" tab; `concertsEventsClient` — same
   *  class, `bookingKind: 'concerts'` — points at concerts-service and hits the identical route
   *  shape for the "Concerts" tab). `status` is the tab selector — 'active' groups
   *  confirmed+pending+on-hold, 'other' groups cancelled+expired+failed. memberId is resolved
   *  server-side from the session cookie — never sent by the client. */
  async getBookings(params: {
    status?: 'active' | 'other';
    page?: number;
    pagesize?: number;
    eventType?: 'sport' | 'concert';
  } = {}): Promise<SportsBookingsListResponse> {
    const r = await this.get<Omit<SportsBookingsListResponse, 'records'> & { records: Booking[] }>(
      '/bookings/list',
      {
        ...(params.status ? { status: params.status } : {}),
        ...(params.page ? { page: String(params.page) } : {}),
        ...(params.pagesize ? { pagesize: String(params.pagesize) } : {}),
        ...(params.eventType ? { eventType: params.eventType } : {}),
      },
    );
    return {
      records: r.records.map((b) => ({ ...b, bookingType: this.bookingKind })),
      total: r.total,
      limit: r.limit,
      page: r.page,
      totalPages: r.totalPages,
    };
  }

  /** Booking detail. Pass `memberId` to scope ownership (Decision 3). */
  async getBooking(id: string, memberId?: string): Promise<Booking> {
    return this.get<Booking>(`/bookings/${id}`, memberId ? { memberId } : undefined);
  }

  async holdBooking(id: string): Promise<Booking> {
    return this.patch<Booking>(`/bookings/${id}/hold`, {});
  }

  async confirmBooking(id: string): Promise<Booking> {
    return this.patch<Booking>(`/bookings/${id}/confirm`, {});
  }

  /** Cancel a booking. Pass `memberId` to scope ownership (Decision 3). */
  async cancelBooking(id: string, memberId?: string): Promise<Booking> {
    const qs = memberId ? `?memberId=${encodeURIComponent(memberId)}` : '';
    return this.patch<Booking>(`/bookings/${id}/cancel${qs}`, {});
  }

  // Payments
  async createCheckoutSession(bookingId: string): Promise<CheckoutSession> {
    return this.post<CheckoutSession>(`/payments/checkout/${bookingId}`, {});
  }

  /** Server-verified payment confirmation. Called from the success page so
   *  the booking is finalised even when the Stripe webhook hasn't reached
   *  the backend. Caller MUST pass the Stripe `sessionId` (Checkout flow)
   *  or `paymentIntentId` (PaymentIntent flow) from the success URL — the
   *  backend verifies it matches the value stored on the booking before
   *  doing anything. Idempotent. */
  async confirmPayment(
    bookingId: string,
    proof: { sessionId?: string; paymentIntentId?: string },
  ): Promise<{ paymentStatus: string; bookingStatus: string }> {
    return this.post<{ paymentStatus: string; bookingStatus: string }>(
      `/payments/confirm/${bookingId}`,
      proof,
    );
  }

  /** List of countries from the local `SportsEvents_Countries` collection.
   *  Used by the checkout form's country dropdown — the ids must match
   *  what SE365 expects when creating an order. */
  async getCountries(): Promise<{ message: string; data: Country[] }> {
    return this.get<{ message: string; data: Country[] }>('/countries');
  }

  async createPaymentIntent(bookingId: string): Promise<PaymentIntent> {
    return this.post<PaymentIntent>(`/payments/intent/${bookingId}`, {});
  }
}

export const sportsEventsClient = new SportsEventsClient();

/**
 * Concerts-service singleton. Same class + endpoint shape as the sports
 * client (concerts-service exposes an identical `/bookings/*` surface over
 * its own `Concert_Orders` collection — a physically separate service/
 * collection from sports, not a shared one) — only the base URL and
 * `bookingKind` differ. Falls back to `NEXT_PUBLIC_SPORTS_EVENTS_API_URL`
 * for the developer sugar case where a single dev box hosts both backends
 * on the same host; every real deployment sets `NEXT_PUBLIC_CONCERTS_API_URL`
 * explicitly.
 */
export const concertsEventsClient = new SportsEventsClient(
  process.env.NEXT_PUBLIC_CONCERTS_API_URL ||
    process.env.NEXT_PUBLIC_SPORTS_EVENTS_API_URL,
  'concerts',
);

